import { vars } from '../../theme';
import {
    TIconButtonSize,
    TIconButtonTheme
} from '../HBIconButton/HBIconButton';
import styled, { css } from 'styled-components';
import { applyOpacity } from '../../utils/color.utils';
import { merge } from 'lodash-es';
import { inertAttr } from '../../utils/shared.utils';
import { focusState } from '../../styles/mixins';
import { CSSDimensionValue } from '../../tokens/types';
// Figma: All variants: https://www.figma.com/file/R2XdHLuoVV8ScYIoH2Pm0Y/Bluxome-UI?node-id=1901-7556&t=xZv9Qg3XvJiJCECT-4
// Figma: Spec document: https://www.figma.com/file/IWWOK7MRPt8QBZVws6qWQg/Bluxome-Guidelines?node-id=1511-41081&t=wQenLLTfxVHEDWmq-4

const SIZES: Record<TIconButtonSize, { size: number }> = {
    xxs: {
        size: 20
    },
    xs: {
        size: 30
    },
    sm: {
        size: 36
    },
    md: {
        size: 42
    },
    lg: {
        size: 48
    },
    xl: {
        size: 48
    }
};

const LIGHT = {
    color: vars.colors.text.invertedPrimary,
    hover: {
        color: vars.colors.text.invertedPrimary
    },
    selected: {
        color: vars.colors.text.invertedPrimary
    }
} as const;

const NEUTRAL = {
    color: vars.colors.text.primary,
    hover: {
        color: vars.colors.text.primary
    },
    selected: {
        color: vars.colors.text.primary
    }
} as const;

const SUBTLE = {
    color: vars.colors.text.tertiary,
    hover: {
        color: vars.colors.text.tertiary
    },
    selected: {
        color: vars.colors.text.tertiary
    }
} as const;

const ACCENT = {
    color: vars.colors.text.accent,
    hover: {
        color: vars.colors.text.accent
    },
    selected: {
        color: vars.colors.text.accent
    }
} as const;

const DEFAULT_SHADOW = {
    boxShadow: vars.shadow['20']
} as const;

const ACTIVE_SHADOW = {
    boxShadow: vars.shadow['20']
} as const;

const FLOATING = {
    ...DEFAULT_SHADOW,
    hover: ACTIVE_SHADOW,
    selected: ACTIVE_SHADOW,
    disabled: DEFAULT_SHADOW
} as const;

const INVISIBLE_CHARCOAL = {
    background: 'transparent',
    hover: {
        background: applyOpacity(vars.colors.surface.itemInverted, 4)
    },
    selected: {
        background: applyOpacity(vars.colors.surface.itemInverted, 8)
    },
    disabled: {
        color: vars.colors.text.disabled,
        background: 'transparent'
    }
} as const;

const INVISIBLE_WHITE = {
    background: 'transparent',
    hover: {
        background: applyOpacity(vars.colors.surface.item, 16)
    },
    selected: {
        background: applyOpacity(vars.colors.surface.item, 25)
    },
    disabled: {
        color: applyOpacity(vars.colors.text.invertedPrimary, 30),
        background: 'transparent'
    }
} as const;

const SOLID_CHARCOAL = {
    background: applyOpacity(vars.colors.surface.itemInverted, 4),
    hover: {
        background: applyOpacity(vars.colors.surface.itemInverted, 8)
    },
    selected: {
        background: applyOpacity(vars.colors.surface.itemInverted, 12)
    },
    disabled: {
        color: vars.colors.text.disabled,
        background: 'transparent'
    }
} as const;

const FLOATING_CHARCOAL = merge({}, FLOATING, {
    background: vars.colors.surface.item,
    hover: {
        background: vars.colors.surface.itemHover
    },
    selected: {
        background: vars.colors.surface.itemHover
    },
    disabled: {
        color: vars.colors.text.disabled,
        background: vars.colors.surface.item
    }
} as const);

interface ThemeStyle {
    color: string;
    background: string;
    boxShadow?: string;
}

const STYLES: Record<
    TIconButtonTheme,
    ThemeStyle & {
        hover: ThemeStyle;
        selected: ThemeStyle;
        disabled: ThemeStyle;
    }
> = {
    'light-invisible': merge({}, LIGHT, INVISIBLE_WHITE),

    'neutral-invisible': merge({}, NEUTRAL, INVISIBLE_CHARCOAL),
    'neutral-solid': merge({}, NEUTRAL, SOLID_CHARCOAL),
    'neutral-floating': merge({}, NEUTRAL, FLOATING_CHARCOAL),

    'subtle-invisible': merge({}, SUBTLE, INVISIBLE_CHARCOAL),
    'subtle-solid': merge({}, SUBTLE, SOLID_CHARCOAL),
    'subtle-floating': merge({}, SUBTLE, FLOATING_CHARCOAL),

    'accent-invisible': merge({}, ACCENT, {
        background: 'transparent',
        hover: {
            background: applyOpacity(vars.colors.base.accent, 6)
        },
        selected: {
            background: applyOpacity(vars.colors.base.accent, 12)
        },
        disabled: {
            color: applyOpacity(vars.colors.text.accent, 30),
            background: 'transparent'
        }
    }),
    'accent-solid': {
        color: vars.colors.text.invertedPrimary,
        background: vars.colors.base.accent,
        hover: {
            color: vars.colors.text.invertedPrimary,
            background: vars.colors.base.accentHover
        },
        selected: {
            color: vars.colors.text.invertedPrimary,
            background: vars.colors.base.accentHover
        },
        disabled: {
            color: vars.colors.text.invertedPrimary,
            background: applyOpacity(vars.colors.base.accent, 30)
        }
    },
    'accent-floating': merge({}, ACCENT, FLOATING, {
        background: vars.colors.surface.item,
        hover: {
            background: applyOpacity(vars.colors.base.accent, 5)
        },
        selected: {
            background: applyOpacity(vars.colors.base.accent, 8)
        },
        disabled: {
            color: applyOpacity(vars.colors.text.accent, 30),
            background: vars.colors.surface.item
        }
    }),

    'error-solid': {
        color: vars.colors.text.danger,
        background: applyOpacity(vars.colors.base.danger, 15),
        hover: {
            color: vars.colors.text.danger,
            background: applyOpacity(vars.colors.base.danger, 25)
        },
        selected: {
            color: vars.colors.text.danger,
            background: applyOpacity(vars.colors.base.danger, 35)
        },
        disabled: {
            color: applyOpacity(vars.colors.text.danger, 30),
            background: 'transparent'
        }
    }
};

function getThemeStyle({ theme, loading }: Props) {
    const { hover, selected, disabled, ...style } = STYLES[theme];

    return css`
        &,
        &:focus {
            ${setStyles(style)};
        }
        &&:hover {
            ${setStyles(hover)};
        }
        ${focusState()}
        &&:active,
        &&[data-state='open'],
        &&.selected {
            ${setStyles(selected)}
        }
        &&&:disabled {
            cursor: not-allowed;
            ${setStyles(disabled)}
        }
        &&&& {
            ${loading && setStyles(disabled)}
        }
    `;
}

function setStyles(style: ThemeStyle) {
    return css`
        background-color: ${style.background};

        // from honeybook-icons to preserve fill color when useDefaultFill is true
        &:not(:has([data-hbui-preserve-fill])) svg * {
            fill: ${style.color};
        }

        &,
        * {
            color: ${style.color};
        }

        ${style.boxShadow &&
        css`
            box-shadow: ${style.boxShadow};
        `}
    `;
}

interface Props {
    disabled: boolean;
    loading: boolean;
    size: TIconButtonSize;
    theme: TIconButtonTheme;
    $margin: CSSDimensionValue;
}

const TRANSITION_DURATION = '80ms';

export const IconButton = styled.button.attrs(inertAttr)<Props>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: ${vars.radius.xs};
    transition-duration: ${TRANSITION_DURATION};
    transition-property: background-color, box-shadow;
    position: relative;

    // Revert angular styles
    &&& {
        border: none;
        ${props =>
            css`
                margin: ${props.$margin || 0};
            `}
    }

    i {
        display: flex;
        svg * {
            transition: fill ${TRANSITION_DURATION};
        }
    }

    &,
    * {
        transition: color ${TRANSITION_DURATION};
    }

    ${props => {
        const size = `${SIZES[props.size].size}px`;
        return css`
            min-width: ${size};
            max-width: ${size};
            min-height: ${size};
            max-height: ${size};
            padding: 0;
        `;
    }}

    ${getThemeStyle}
`;
